import { ChangeEvent, FC, useCallback, useMemo, useState } from "react";
import clsx from "clsx";

import { IconButton } from "@mui/material";

import RoundedInfo from "icons/RoundedInfo";
import RoundedCheck from "icons/RoundedCheck";
import Eye from "icons/Eye";
import ClosedEye from "icons/ClosedEye";

import {
  StyledInput,
  StyledInputContainer,
  StyledInputLabel,
  StyledInputError,
  StyledInputAdornment,
} from "styles/components/inputs/Input";

interface InputPasswordProps {
  id?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  defaultValue?: string;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  error?: boolean;
  success?: boolean;
  required?: boolean;
  errorText?: string;
  autoFocus?: boolean;
  dataTestId?: string;
  withErrorField?: boolean;

  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const InputPassword: FC<InputPasswordProps> = ({
  label = "Label",
  id = "initial-input-password-id",
  value,
  defaultValue,
  placeholder = "Placeholder",
  disabled,
  error,
  success,
  className,
  inputClassName,
  required,
  errorText = "Error text",
  autoFocus,
  dataTestId,
  withErrorField = true,
  onChange,
  onBlur,
  onFocus,
}) => {
  const [passwordIsVisible, setPasswordIsVisible] = useState<boolean>(false);

  const handlePasswordToggle = useCallback(
    () => setPasswordIsVisible((prev) => !prev),
    []
  );

  const wrapClassName = clsx(["input-container", className]);

  const fieldClassName = clsx([
    "input input-password",
    { input_success: success },
    inputClassName,
  ]);

  const errorClassName = clsx("input-error", { "input-error_visible": error });

  const renderedLabel = useMemo(
    () =>
      label ? (
        <StyledInputLabel
          htmlFor={id}
          disabled={disabled}
          required={required}
          data-testid={dataTestId && `${dataTestId}-label`}
        >
          {label}
        </StyledInputLabel>
      ) : null,
    [dataTestId, disabled, id, label, required]
  );

  const renderedSuffix = useMemo(() => {
    if (error) {
      return (
        <StyledInputAdornment
          position="end"
          data-testid={dataTestId && `${dataTestId}-error-icon`}
        >
          <RoundedInfo filled={false} />
        </StyledInputAdornment>
      );
    }

    if (success) {
      return (
        <StyledInputAdornment
          position="end"
          data-testid={dataTestId && `${dataTestId}-success-icon`}
        >
          <RoundedCheck />
        </StyledInputAdornment>
      );
    }

    return (
      <StyledInputAdornment position="end">
        <IconButton
          size="small"
          className="input-password-btn"
          onClick={handlePasswordToggle}
          data-testid={dataTestId && `${dataTestId}-btn`}
        >
          {passwordIsVisible ? <ClosedEye /> : <Eye />}
        </IconButton>
      </StyledInputAdornment>
    );
  }, [dataTestId, error, handlePasswordToggle, passwordIsVisible, success]);

  const renderedErrorField = useMemo(
    () =>
      withErrorField ? (
        <StyledInputError
          variant="bodyM"
          fontWeight={300}
          className={errorClassName}
          data-testid={dataTestId && `${dataTestId}-error`}
        >
          {errorText}
        </StyledInputError>
      ) : null,
    [dataTestId, errorClassName, errorText, withErrorField]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value),
    [onChange]
  );

  return (
    <StyledInputContainer className={wrapClassName} data-testid={dataTestId}>
      {renderedLabel}
      <StyledInput
        id={id}
        required={required}
        disabled={disabled}
        error={error}
        type={passwordIsVisible ? "text" : "password"}
        className={fieldClassName}
        endAdornment={renderedSuffix}
        value={value}
        defaultValue={defaultValue}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={handleInputChange}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid={dataTestId && `${dataTestId}-input`}
      />
      {renderedErrorField}
    </StyledInputContainer>
  );
};

export default InputPassword;
